import React, { useState, useEffect, useMemo } from 'react';
import API from '../../services/api';
import NoDataFound from '../../shared/NoDataFound';
import TableLoader from '../../shared/TableLoader';
import Paginator from '../../shared/Paginator';
import config from '../../config';
import { SlButton, SlDrawer } from '@shoelace-style/shoelace/dist/react';
import { formatDateWithTimeYear } from '../../utils/dateFormat';
import PreviewFrameEdit from '../PreviewFrameEdit';
import { types } from '../../utils/constants';
import LineitemPreview from '../campaign/LineitemPreview';
import { roles } from '../../utils/constants';
import createdSVG from '../../assets/images/created.svg';
import runningSVG from '../../assets/images/running.svg';
import pausedSVG from '../../assets/images/paused.svg';
import completedSVG from '../../assets/images/completed.svg';
import scheduledSVG from '../../assets/images/scheduled.svg';
import approvedSVG from '../../assets/images/request.svg';

const statusIconMap = {
    1: { src: createdSVG, label: 'Created' },
    2: { src: runningSVG, label: 'Running' },
    3: { src: pausedSVG, label: 'Paused' },
    4: { src: completedSVG, label: 'Completed' },
    5: { src: scheduledSVG, label: 'Scheduled' },
    6: { src: approvedSVG, label: 'Approved' },
};
export const BackfillReportContext = React.createContext();

const isAdvertiser = localStorage.getItem('role') === roles.advertiser;

function SpecialAdReport({ dateFilter, search }) {
    const [specialAds, setSpecialAds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [showLeadsCampaignId, setShowLeadsCampaignId] = useState(null);
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [leadsCurrentPage, setLeadsCurrentPage] = useState(0);
    const [leadsTotalPages, setLeadsTotalPages] = useState(0);
    const [downloadingCampaignId, setDownloadingCampaignId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [campaignDrawerId, setCampaignDrawerId] = useState(null);
    const fetchSpecialAds = async () => {
        setLoading(true);
        try {
            const response = await API({
                url: '/reports/special_ad_report',
                method: 'POST',
                responseType: 'json',
                data: {
                    date_filter: dateFilter,
                    search: search,
                    limit: config.PER_PAGE,
                    page: currentPage,
                }
            });
            setSpecialAds(response.data?.data || []);
            setTotalPages(response.data?.total ? Math.ceil(response.data?.total / config.PER_PAGE) : 0);
            setTotalCount(response.data?.total || 0);
        } catch (error) {
            console.error(error);
            setSpecialAds([]);
            setTotalPages(0);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }

    const fetchPreview = async (id) => {
    try {
      let res = await API({
        url: "/inventory/get-preview",
        method: "POST",
        responseType: "json",
        data: {
          campaign_id: id,
        },
      });
      if (res.code === 200) {
        setPreview(res?.data);
        setShowPreview(true);
      }
    } catch (error) {
      console.error(error);
    }
  };
  function handleRequestClose(event) {
    if (event.detail.source === "overlay") {
      event.preventDefault();
    }
  }
    const fetchLeads = async (campaignId) => {
        setLeadsLoading(true);
        try {
            const response = await API({
                url: '/reports/special_ad_leads',
                method: 'POST',
                data: {
                    campaign_id: campaignId,
                    page: leadsCurrentPage,
                    limit: config.PER_PAGE,
                    date_filter: dateFilter,
                }
            });
            const payload = response?.data;
            const leadsData = Array.isArray(payload)
                ? payload
                : payload?.result || payload?.data || [];
            const totalRecords = payload?.total ?? payload?.count ?? leadsData.length;
            if(Array.isArray(leadsData)){
                let tempLeads = leadsData.map((lead) => {
                    delete lead.form_details?.creative_id;
                    delete lead.form_details?.special_id;
                    delete lead.form_details?.form_type;
                    return lead;
                });
                setLeads(tempLeads || []);
            } else {
                setLeads([]);
            }
            setLeadsTotalPages(totalRecords ? Math.ceil(totalRecords / config.PER_PAGE) : 0);
        } catch (error) {
            console.error(error);
            setLeads([]);
            setLeadsTotalPages(0);
        } finally {
            setLeadsLoading(false);
        }
    }

    useEffect(() => {
        setLeadsCurrentPage(0);
        setShowLeadsCampaignId(null);
        fetchSpecialAds();
    }, [dateFilter, search, currentPage]);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    }
    const handleLeadsPageClick = (event) => {
        setLeadsCurrentPage(event.selected);
    }

    const formatLeadDetails = (lead) => {
        if (!lead?.form_details || typeof lead.form_details !== 'object') {
            return '-';
        }
        const entries = Object.entries(lead.form_details)
            .filter(([, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => `${key}: ${value}`);
        return entries.length ? entries.join(' | ') : '-';
    };

    const formatPaymentMethod = (lead) => {
        const method = lead?.payment_method;
        if (!method || typeof method !== 'object') {
            return '-';
        }
        if (method.upi?.upi_id) {
            return `UPI (${method.upi.upi_id})`;
        }
        return Object.keys(method).join(', ') || '-';
    };

    const formatLeadTime = (createdAt) => {
        if (!createdAt) {
            return '-';
        }
        const epochValue = Number(createdAt);
        if (!Number.isNaN(epochValue)) {
            return formatDateWithTimeYear(epochValue);
        }
        return formatDateWithTimeYear(createdAt);
    };

    const formatCampaignDate = (epochValue) => {
        if (!epochValue) {
            return '-';
        }
        const value = Number(epochValue);
        if (Number.isNaN(value)) {
            return '-';
        }
        return new Date(value * 1000).toLocaleDateString('en-IN');
    };

    const leadFormKeys = useMemo(() => {
        const keys = [];
        const seen = new Set();

        leads.forEach((lead) => {
            const formDetails = lead?.form_details;
            if (!formDetails || typeof formDetails !== 'object' || Array.isArray(formDetails)) {
                return;
            }
            Object.keys(formDetails).forEach((key) => {
                if (!seen.has(key)) {
                    seen.add(key);
                    keys.push(key);
                }
            });
        });

        return keys;
    }, [leads]);

    const sanitizeFileName = (value) => {
        if (!value) {
            return 'special-ad-leads-report';
        }
        return String(value)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'special-ad-leads-report';
    };

    const getLeadsArrayFromPayload = (payload) => {
        if (Array.isArray(payload)) {
            return payload;
        }
        return payload?.result || payload?.data || [];
    };

    const getUniqueFormKeys = (records) => {
        const keys = [];
        const seen = new Set();

        records.forEach((lead) => {
            const formDetails = lead?.form_details;
            if (!formDetails || typeof formDetails !== 'object' || Array.isArray(formDetails)) {
                return;
            }
            Object.keys(formDetails).forEach((key) => {
                if (!seen.has(key)) {
                    seen.add(key);
                    keys.push(key);
                }
            });
        });

        return keys;
    };

    const escapeCsvValue = (value) => {
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const downloadLeadsReport = async (campaign) => {
        if (!campaign?.campaign_id) {
            return;
        }

        setDownloadingCampaignId(campaign.campaign_id);
        try {
            const response = await API({
                url: '/reports/special_ad_leads',
                method: 'POST',
                data: {
                    campaign_id: campaign.campaign_id,
                    date_filter: dateFilter,
                }
            });

            let allLeads = getLeadsArrayFromPayload(response?.data);
            if (!Array.isArray(allLeads) || !allLeads.length) {
                return;
            }
            allLeads = allLeads.map((lead) => {
                delete lead.form_details?.creative_id;
                delete lead.form_details?.special_id;
                delete lead.form_details?.form_type;
                return lead;
            });
            const dynamicFormKeys = getUniqueFormKeys(allLeads);
            const headers = ['Cust Id', ...dynamicFormKeys, 'Time', 'Payment Status', 'Payment Message', 'CF Order Id', 'Payment Method'];
            const rows = allLeads.map((lead) => ([
                lead.cust_id || '-',
                ...dynamicFormKeys.map((key) => lead?.form_details?.[key] ?? '-'),
                formatLeadTime(lead.created_at),
                lead.order_status || '-',
                lead.payment_message || '-',
                lead.cf_order_id || '-',
                formatPaymentMethod(lead),
            ]));

            const csvContent = [headers, ...rows]
                .map((row) => row.map(escapeCsvValue).join(','))
                .join('\n');

            const fileName = `${sanitizeFileName(campaign.campaign_name)}-report.csv`;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        } finally {
            setDownloadingCampaignId(null);
        }
    };

    useEffect(() => {
        if (showLeadsCampaignId) {
            fetchLeads(showLeadsCampaignId);
        }
    }, [showLeadsCampaignId, leadsCurrentPage]);

    return (
        <>
        <div className="card shadow-sm border-0">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                    <h5 className="mb-2 mb-md-0">Backfill Ad Report</h5>
                    <div className="d-flex gap-2">
                        <span className="badge bg-primary">Campaigns: {totalCount}</span>
                        {/* {showLeadsCampaignId ? <span className="badge bg-info text-dark">Viewing Leads: {showLeadsCampaignId}</span> : null} */}
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-2 tableNew">
                        <thead>
                            <tr className="table-light">
                                <th>Status</th>
                                <th>Campaign name</th>
                                <th>Creative Type</th>
                                {!isAdvertiser && <th>Actual Imp/Click</th>}
                                <th>Adv Imp/Click</th>
                                <th>CTR(%)</th>
                                <th>Total leads</th>
                                <th>Payment Initiated</th>
                                <th>Paid</th>
                                <th>Failed</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {specialAds.length > 0 && !loading ? (specialAds.map((specialAd, index) => {
                                const isExpanded = showLeadsCampaignId === specialAd.campaign_id;
                                const hasLeads = Number(specialAd?.total_records) > 0;
                                return (
                                    <React.Fragment key={specialAd.campaign_id ?? index}>
                                        <tr
                                            key={specialAd.campaign_id ?? index}
                                            onClick={() => {
                                                if (!hasLeads) {
                                                    return;
                                                }
                                                setLeadsCurrentPage(0);
                                                setShowLeadsCampaignId(isExpanded ? null : specialAd.campaign_id);
                                            }}
                                            style={{ cursor: hasLeads ? 'pointer' : 'not-allowed' }}
                                        >
                                            <td className="text-center">
                                                {statusIconMap[specialAd.status] ? (
                                                    <sl-tooltip content={statusIconMap[specialAd.status].label} hoist>
                                                        <img className="svg-icon20" src={statusIconMap[specialAd.status].src} alt={statusIconMap[specialAd.status].label} />
                                                    </sl-tooltip>
                                                ) : '-'}
                                            </td>
                                            <td>
                                                <div className="fw-semibold">{specialAd.campaign_name || '-'}</div>
                                                <small className="text-muted">Campaign ID: {specialAd.campaign_id || '-'}</small>
                                            </td>
                                            <td>{types.find(type => type.value === specialAd.creative_type)?.label || '-'}</td>
                                            {!isAdvertiser && <td>
                                                <div className="splitView">
                                                    <p>{specialAd.total_imp ? Math.floor(specialAd.total_imp): 0}</p>
                                                    <div class="devider">   </div>
                                                    <p> {specialAd.total_click ? Math.floor(specialAd.total_click): 0}</p>
                                                </div>
                                            </td>}
                                            <td>
                                                <div className="splitView">
                                                    <p>{specialAd.adv_total_imp ? Math.floor(specialAd.adv_total_imp): 0}</p>
                                                    <div class="devider">   </div>
                                                    <p> {specialAd.adv_total_click ? Math.floor(specialAd.adv_total_click): 0}</p>
                                                </div>
                                            </td>
                                            <td>{specialAd.total_click > 0 && specialAd.total_imp > 0 ? ((specialAd.total_click / specialAd.total_imp) * 100).toFixed(2) : 0}%
                                            </td>
                                            <td>{specialAd.total_records}</td>
                                            <td>{specialAd.payment_orders}</td>
                                            <td>{specialAd.paid_count}</td>
                                            <td>{specialAd.failed_count}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <SlButton
                                                        variant={isExpanded ? 'default' : 'primary'}
                                                        disabled={!hasLeads}
                                                    >
                                                        {!hasLeads ? 'No Leads' : (isExpanded ? 'Hide Leads' : 'View Leads')}
                                                    </SlButton>

                                                    <SlButton variant='primary' onClick={(e) => { e.stopPropagation(); setCampaignDrawerId(specialAd.campaign_id); }}>Campaign</SlButton>
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && hasLeads ? (
                                            <tr>
                                                <td colSpan="14">
                                                    <div className="bg-light rounded border p-3 my-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <h6 className="mb-0">Leads</h6>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="text-muted small">Campaign ID: {specialAd.campaign_id}</span>
                                                                <SlButton
                                                                    size="small"
                                                                    variant="success"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        downloadLeadsReport(specialAd);
                                                                    }}
                                                                    disabled={downloadingCampaignId === specialAd.campaign_id}
                                                                >
                                                                    {downloadingCampaignId === specialAd.campaign_id ? 'Downloading...' : 'Download Leads'}
                                                                </SlButton>
                                                            </div>
                                                        </div>
                                                        <div className="overflow-x-auto">
                                                            <table className="table table-bordered table-sm align-middle mb-2">
                                                            <thead>
                                                                <tr className="table-light">
                                                                    <th>Cust Id</th>
                                                                    {leadFormKeys.map((key) => (
                                                                        <th key={key}>{key}</th>
                                                                    ))}
                                                                    <th>Time</th>
                                                                    <th>Amount Received</th>
                                                                    <th>Payment Status</th>
                                                                    <th>Payment Message</th>
                                                                    <th>CF Order Id</th>
                                                                    <th>Payment Method</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {leads.length > 0 && !leadsLoading ?
                                                                    (leads.map((lead) => (
                                                                        <tr key={lead.form_id}>
                                                                            <td>{lead.cust_id || '-'}</td>
                                                                            {leadFormKeys.map((key) => (
                                                                                <td key={`${lead.form_id || lead.cust_id || 'lead'}-${key}`}>
                                                                                    {lead?.form_details?.[key] ?? '-'}
                                                                                </td>
                                                                            ))}
                                                                            <td>{formatLeadTime(lead.created_at)}</td>
                                                                            <td>{lead.order_status === 'SUCCESS' ? `â‚¹${lead.order_amount}` : '-'}</td>
                                                                            <td>{lead.order_status || '-'}</td>
                                                                            <td>{lead.payment_message || '-'}</td>
                                                                            <td>{lead.cf_order_id || '-'}</td>
                                                                            <td>{formatPaymentMethod(lead)}</td>
                                                                        </tr>
                                                                    ))) : !leads.length && !leadsLoading ? <NoDataFound colspan={leadFormKeys.length + 6} /> : <TableLoader colspan={leadFormKeys.length + 6} />}
                                                            </tbody>
                                                        </table>
                                                        {leadsTotalPages > 1 ? (
                                                            <Paginator pageCount={leadsTotalPages} handlePageClick={handleLeadsPageClick} forcePage={leadsCurrentPage} />
                                                        ) : null}
                                                    </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>

                                )
                            })
                            ) : !specialAds.length && !loading ? (
                                <NoDataFound colspan="14" />
                            ) : (
                                <TableLoader colspan="14" />
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 ? (
                    <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />
                ) : null}
            </div>
        </div>
        {showPreview && (
            <SlDrawer open={showPreview} onSlRequestClose={handleRequestClose} placement="end" onSlAfterHide={() => { setShowPreview(false); setPreview({}); }}>
                <BackfillReportContext.Provider value={{ preview, setPreview }}>
                    <PreviewFrameEdit showPreview={showPreview} />
                </BackfillReportContext.Provider>
            </SlDrawer>
        )}

        {campaignDrawerId && (
            <SlDrawer
                label="Campaign Details"
                open={!!campaignDrawerId}
                onSlAfterHide={() => setCampaignDrawerId(null)}
                style={{ "--size": window.innerWidth <= 767 ? "100vw" : "65vw" }}
            >
                <LineitemPreview campaign_id={campaignDrawerId} />
                <SlButton slot="footer" variant="primary" onClick={() => setCampaignDrawerId(null)}>Close</SlButton>
            </SlDrawer>
        )}
        </>
    );
}
export default SpecialAdReport;